import React, { useEffect, useMemo, useState } from 'react';

import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type { SeatInfo } from '../charter.types';

import {
  MANUAL_BLOCK_REASON_LABELS,
  type ManualBlockReason,
  type SeatOverrideFormValue,
} from './CharterSeatConfigurationOverridesHelpers';

type Props = {
  open: boolean;
  seat: SeatInfo | null;
  baseSeat: SeatInfo | null;
  isOccupied: boolean;
  onClose: () => void;
  onSave: (value: SeatOverrideFormValue) => void;
  isSaving: boolean;
};

export default function CharterSeatOverrideDialog({
  open,
  seat,
  baseSeat,
  isOccupied,
  onClose,
  onSave,
  isSaving,
}: Props): React.JSX.Element {
  const [isAvailable, setIsAvailable] = useState(true);
  const [blockReason, setBlockReason] = useState<ManualBlockReason | ''>('');
  const [price, setPrice] = useState('');

  useEffect(() => {
    if (!seat) {
      return;
    }

    setIsAvailable(seat.isAvailable);
    setBlockReason(seat.blockReason && seat.blockReason !== 'occupied' ? (seat.blockReason as ManualBlockReason) : '');
    setPrice(String(seat.price ?? 0));
  }, [seat]);

  const parsedPrice = Number(price);
  const priceError = !Number.isFinite(parsedPrice) || parsedPrice < 0;
  const blockReasonError = !isAvailable && !blockReason;
  const isSaveDisabled = !seat || !baseSeat || isOccupied || priceError || blockReasonError || isSaving;

  const statusText = useMemo(() => {
    if (isOccupied) {
      return 'Booked / occupied';
    }
    return seat?.isAvailable ? 'Available' : 'Blocked';
  }, [isOccupied, seat?.isAvailable]);

  const handleResetToBase = () => {
    if (!baseSeat) {
      return;
    }

    setIsAvailable(baseSeat.isAvailable);
    setBlockReason(
      baseSeat.blockReason && baseSeat.blockReason !== 'occupied' ? (baseSeat.blockReason as ManualBlockReason) : '',
    );
    setPrice(String(baseSeat.price ?? 0));
  };

  const handleSave = () => {
    if (!seat || !baseSeat || isSaveDisabled) {
      return;
    }

    onSave({
      isAvailable,
      blockReason: isAvailable ? undefined : (blockReason as ManualBlockReason),
      price: parsedPrice,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{seat ? `Seat ${seat.displayLabel ?? seat.seatId}` : 'Seat override'}</DialogTitle>
      <DialogContent>
        {seat && baseSeat ? (
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                Current status
              </Typography>
              <Typography variant="subtitle1" fontWeight={600}>
                {statusText}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Base price ${baseSeat.price ?? 0}
              </Typography>
            </Stack>

            {isOccupied ? (
              <Alert severity="info">
                This seat is booked. Block status and seat price are managed by the existing seat selection.
              </Alert>
            ) : null}

            <FormControl disabled={isOccupied}>
              <RadioGroup
                row
                value={isAvailable ? 'available' : 'blocked'}
                onChange={(_, value) => setIsAvailable(value === 'available')}
              >
                <FormControlLabel value="available" control={<Radio />} label="Unblocked" />
                <FormControlLabel value="blocked" control={<Radio />} label="Blocked" />
              </RadioGroup>
            </FormControl>

            {!isAvailable ? (
              <FormControl required error={blockReasonError} disabled={isOccupied}>
                <InputLabel id="seat-block-reason-label">Block reason</InputLabel>
                <Select
                  labelId="seat-block-reason-label"
                  label="Block reason"
                  value={blockReason}
                  onChange={(event) => setBlockReason(event.target.value as ManualBlockReason)}
                >
                  {Object.entries(MANUAL_BLOCK_REASON_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
                {blockReasonError ? <FormHelperText>Block reason is required.</FormHelperText> : null}
              </FormControl>
            ) : null}

            <TextField
              label="Seat price"
              value={price}
              type="number"
              disabled={isOccupied}
              onChange={(event) => setPrice(event.target.value)}
              error={priceError}
              helperText={
                isOccupied
                  ? 'Booked seat prices cannot be changed.'
                  : priceError
                    ? 'Price must be a non-negative number.'
                    : ' '
              }
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleResetToBase} disabled={!baseSeat || isOccupied || isSaving}>
          Reset to base
        </Button>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <LoadingButton variant="contained" onClick={handleSave} loading={isSaving} disabled={isSaveDisabled}>
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
